import styles from "./AssetPanelHeader.module.scss";
import {
  canBeOpenedInReader,
  isExpiringSoon,
} from "../../../../common/features/asset/assetHelpers.ts";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import { getReaderNavLink } from "../../../bookshelfLinks.ts";
import AssetTypeTags from "../../../../common/features/asset/AssetTypeTags.tsx";
import NthEdition from "../../../../common/features/asset/NthEdition.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import useStoreLinks from "../../../../common/features/store/useStoreLinks.ts";
import ExpiringSoonMessage from "../../../../common/features/asset/ExpiringSoonMessage.tsx";
import {
  License,
  ManageAsset,
} from "../../../../common/api/decimal/common-types/manageAssets.ts";
import BorrowOrOpenBookButton from "../explore/BorrowOrOpenBookButton.tsx";
import DefaultCoverBadge from "../../../../common/features/asset/DefaultCoverBadge.tsx";
import useAssetActivate from "../../actions/explore/activate/useAssetActivate.tsx";
import {
  ExploreAssetAction,
  useExploreAssetActionType,
} from "../explore/useExploreAssetActionType.ts";
import EmptyAssetCard from "../../../../common/features/asset/EmptyAssetCard.tsx";
import { useMemo } from "react";

export default function AssetPanelHeader({
  asset,
  license,
  explore,
  returnDetail,
}: {
  asset?: ManageAsset;
  license?: License;
  explore?: VST.Bridge.Asset;
  returnDetail?: string;
}) {
  // Fall back to explore-only data if we don't have manage data, such as in explore search
  const commonAsset = asset ?? explore!;
  const intl = useIntl();
  const { makeRenewalUrl, isStoreAsset } = useStoreLinks();
  const activateAsset = useAssetActivate(explore);
  const actionType = useExploreAssetActionType(explore);

  const coverAction = useMemo(() => {
    if (
      (!!asset && canBeOpenedInReader(asset, license)) ||
      (explore && actionType === ExploreAssetAction.Open)
    ) {
      return {
        ...getReaderNavLink(commonAsset.vbid, {
          needsActivation: !!explore && !explore.active,
          returnDetail,
        }),
        "aria-label": intl.formatMessage(
          { defaultMessage: "Open Book {title}" },
          { title: commonAsset.title }
        ),
      };
    }
    if (explore && actionType === ExploreAssetAction.Borrow) {
      return {
        onClick: activateAsset,
        "aria-label": intl.formatMessage(
          { defaultMessage: "Borrow {title}" },
          { title: commonAsset.title }
        ),
      };
    }
  }, [
    asset,
    license,
    explore,
    actionType,
    commonAsset.vbid,
    commonAsset.title,
    returnDetail,
    intl,
    activateAsset,
  ]);

  function getTitle() {
    const Wrapper = coverAction ? BaseLinkOrButton : "div";
    const action = coverAction ?? {};

    return (
      <Wrapper className={styles.title} {...action}>
        <h2>{commonAsset.title}</h2>
      </Wrapper>
    );
  }

  return (
    <div className={styles.container}>
      <EmptyAssetCard
        vbid={commonAsset.vbid}
        tagName="div"
        type="horizontal"
        imageChildren={
          <DefaultCoverBadge license={license} explore={explore} />
        }
        primaryAction={coverAction}
        contentAreaClassName={styles.rightContent}
      >
        <AssetTypeTags asset={commonAsset} />
        {getTitle()}
        <div className={styles.subtitle}>{asset?.subtitle}</div>
        <NthEdition asset={commonAsset} className={styles.edition} />
        {license && isExpiringSoon(license) && !explore?.active && (
          <div className={styles.expiringSoonContainer}>
            <ExpiringSoonMessage
              license={license}
              className={styles.expiringSoonAlert}
            />
            {isStoreAsset(license) && (
              <BaseLinkOrButton
                href={makeRenewalUrl(commonAsset.vbid, {
                  utm_medium: "bookshelf-home-extend-details",
                  utm_campaign: commonAsset.vbid,
                })}
                className="vst-link vst-link-size-small"
              >
                <FormattedMessage defaultMessage="Extend" />
              </BaseLinkOrButton>
            )}
          </div>
        )}
        {explore && (
          <BorrowOrOpenBookButton
            asset={explore}
            className={styles.returnButton}
            returnDetail={returnDetail}
          />
        )}
      </EmptyAssetCard>
    </div>
  );
}
